/**
 * Browser Answer Client (Phase 2 — post-merge wiring)
 *
 * Tiny client helper used by the chat-tile delegated click handler to
 * forward `BUTTON_IDS.SUBMIT_ANSWER` clicks to the browser-service:
 *
 *   POST {BASE_API}/api/sessions/:sessionId/answer  { fieldId, value }
 *
 * On success, the browser-service re-emits a frame and the existing
 * frame-stream subscription in browserSessionFrames will trigger a
 * tile re-render (no manual refresh needed).
 *
 * On error (4xx/5xx), the caller surfaces the message inline in the
 * paused tile so the user sees what went wrong.
 *
 * The browser-service base URL is the Firestore-resolved
 * `system/prest0Core/services/main.browserServiceUrl` (see
 * `src/services/browserService/baseUrl.ts` for resolution rules).
 */

import {
  getBrowserServiceBaseUrl,
  getCachedBrowserServiceBaseUrl,
  getBrowserServiceAuthHeaders,
} from '../../browserService/baseUrl';

/**
 * @deprecated Use `getBrowserServiceBaseUrl()` / `getCachedBrowserServiceBaseUrl()`
 * from `src/services/browserService/baseUrl.ts`. Retained as a getter so any
 * external import still resolves to the live cached value at access time.
 */
export const BROWSER_BASE_API_GETTER = (): string =>
  getCachedBrowserServiceBaseUrl();

export interface SubmitAnswerInput {
  sessionId: string;
  fieldId: string;
  value: unknown;
  /** Optional fetch override for tests. Defaults to global fetch. */
  fetchImpl?: typeof fetch;
  /** Optional base URL override for tests. */
  baseApi?: string;
}

export interface SubmitAnswerResult {
  ok: boolean;
  status?: number;
  error?: string;
}

/**
 * Build the POST body for /api/sessions/:sessionId/answer.
 *
 * Exposed (and not inlined into postSubmitAnswer) so unit tests can
 * assert the exact JSON shape without mocking fetch.
 */
export function buildSubmitAnswerPayload(input: {
  fieldId: string;
  value: unknown;
}): { fieldId: string; value: unknown } {
  return { fieldId: input.fieldId, value: input.value };
}

export async function postSubmitAnswer(
  input: SubmitAnswerInput
): Promise<SubmitAnswerResult> {
  const { sessionId, fieldId, value, fetchImpl, baseApi } = input;
  if (!sessionId) return { ok: false, error: 'missing sessionId' };
  if (!fieldId) return { ok: false, error: 'missing fieldId' };

  const fetcher = fetchImpl ?? (typeof fetch !== 'undefined' ? fetch : undefined);
  if (!fetcher) {
    return { ok: false, error: 'fetch unavailable' };
  }
  const base = baseApi ?? (await getBrowserServiceBaseUrl());
  const url = `${base}/api/sessions/${encodeURIComponent(sessionId)}/answer`;
  // 2026-08-11 (P0 security item #6): attach the browser-service bearer token
  // when provisioned (Firestore `system/prest0Core/services/main
  // .browserServiceToken`) — /api/* on the service is now token-gated. While
  // unprovisioned this spreads `{}` and the request is byte-identical to the
  // pre-auth behavior, matching the server's deploy-safe allow-all default.
  const authHeaders = await getBrowserServiceAuthHeaders();
  try {
    const res = await fetcher(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...authHeaders },
      body: JSON.stringify(buildSubmitAnswerPayload({ fieldId, value })),
    });
    if (!res.ok) {
      let body: { error?: string } = {};
      try {
        body = (await res.json()) as { error?: string };
      } catch {
        // ignore — error body may not be JSON
      }
      return {
        ok: false,
        status: res.status,
        error: body.error || `HTTP ${res.status}`,
      };
    }
    return { ok: true, status: res.status };
  } catch (err) {
    return {
      ok: false,
      error: err instanceof Error ? err.message : String(err),
    };
  }
}
